import { AgentIconWithFallback } from 'ai-components-ui-library/components/icons/AgentIconWithFallback';
import { AuthorizationStatus, NotificationStatus } from 'connections-embed-lib';
import { BulbIcon } from 'foundations-assets/icons/components/BulbIcon';
import { FavoriteIcon } from 'foundations-assets/icons/components/FavoriteIcon';
import { HubspotCertificationIcon } from 'foundations-assets/icons/components/HubspotCertificationIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { formatInstalls } from 'marketplace-ui-common/utils/installs';
import { useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getSearchResultClickedEvent, SearchResultViewTracker } from 'marketplace-ui-tracking/events/marketplace';
import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { BreezeBadge, useLazyImage } from 'trellis-ecosystem';
import * as t from 'ts-schema';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { Paths } from '../../constants/paths';
import { getInstalledByCard, getProductTypeByCard, getRecommendationTypeByCard, getSourceIdByCard } from '../../utils/usageTracker';
import { getPartnerTierContent } from '../../utils/getPartnerTierContent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function StorefrontListingCard({
  card: listing,
  // TODO: rename to listing
  collectionId,
  collectionSlug,
  context,
  pageNumber,
  queryType,
  resultCount,
  resultIndex,
  searchTerm,
  section
}) {
  var _listing$installCount;
  const product = listing.products[0];
  const installed = getInstalledByCard(listing);
  const portalInstallable = product === null || product === void 0 ? void 0 : product.portalInstallable;
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const searchResultEventProps = useMemo(() => {
    return Object.assign({
      companyName: listing.companyName,
      componentType: 'MarketplaceCardLarge',
      installed,
      listingId: listing.listingId,
      pageNumber,
      recommendationType: getRecommendationTypeByCard(listing),
      resultCount,
      resultIndex,
      resultType: 'LISTING',
      search: searchTerm,
      section,
      slug: listing.slug,
      sourceId: getSourceIdByCard(listing),
      type: getProductTypeByCard(listing),
      value: context === null || context === void 0 ? void 0 : context.value
    }, queryType && {
      queryType: String(queryType)
    }, collectionId && {
      collectionId,
      category: collectionSlug
    }, portalInstallable && {
      portalInstallable
    });
  }, [listing, collectionId, collectionSlug, context === null || context === void 0 ? void 0 : context.value, installed, pageNumber, portalInstallable, queryType, resultCount, resultIndex, searchTerm, section]);
  const onLinkClick = () => {
    trackMarketplaceEvent(getSearchResultClickedEvent(searchResultEventProps));
  };
  const listingHref = generatePath(Paths.LISTING_DETAILS, {
    listingSlug: t.assert(t.string, listing.slug)
  });
  return /*#__PURE__*/_jsx(SearchResultViewTracker, Object.assign({}, searchResultEventProps, {
    children: /*#__PURE__*/_jsx(RouterLink, {
      use: "unstyled",
      inlineSize: "100%",
      onClick: onLinkClick,
      to: listingHref,
      children: /*#__PURE__*/_jsx(CardWrapper, {
        compact: true,
        marginBlockEnd: "0",
        blockSize: "100%",
        children: /*#__PURE__*/_jsxs(CardSection, {
          display: "flex",
          flexDirection: "column",
          blockSize: "100%",
          children: [listing.betaStage && /*#__PURE__*/_jsx(Ribbon, {
            position: "left",
            use: "beta",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-embed.card.beta"
            })
          }), product.installed && product.productId && /*#__PURE__*/_jsx(NotificationStatus, {
            appId: product.productId,
            type: "ribbon"
          }), /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            backgroundColor: "fill-surface-recessed",
            blockSize: "150px",
            borderRadius: "200",
            justifyContent: "center",
            marginBlockEnd: "500",
            overflow: "hidden",
            position: "relative",
            children: /*#__PURE__*/_jsx(ListingIcon, {
              listing: listing
            })
          }), product.certified && /*#__PURE__*/_jsx(View, {
            insetBlockStart: "600",
            insetInlineEnd: "600",
            padding: "200",
            position: "absolute",
            children: /*#__PURE__*/_jsx(Tooltip, {
              title: unescapedText('marketplace-embed.card.certified'),
              children: /*#__PURE__*/_jsx(HubspotCertificationIcon, {
                "aria-label": unescapedText('marketplace-embed.card.certified'),
                color: "fill-brand-default",
                size: "600"
              })
            })
          }), /*#__PURE__*/_jsx(Text, {
            as: "h4",
            typography: "heading-100",
            truncate: {
              lineClamp: 1
            },
            children: listing.listingName
          }), product.productType !== 'SOLUTIONS_PARTNER_PROFILE' && /*#__PURE__*/_jsxs(Text, {
            as: "small",
            color: "text-core-subtle",
            marginBlockStart: "100",
            children: [/*#__PURE__*/_jsx(Text, {
              as: "span",
              marginInlineEnd: "400",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-embed.card.providerName",
                options: {
                  providerName: listing.companyName
                }
              })
            }), /*#__PURE__*/_jsx(Text, {
              whiteSpace: "nowrap",
              as: "span",
              display: "inline-block",
              children: product.installed && product.productId ? /*#__PURE__*/_jsx(View, {
                marginBlock: "-200",
                children: /*#__PURE__*/_jsx(AuthorizationStatus, {
                  appId: product.productId,
                  showInstalledStatus: true
                })
              }) : formatInstalls((_listing$installCount = listing.installCount) !== null && _listing$installCount !== void 0 ? _listing$installCount : 0)
            })]
          }), /*#__PURE__*/_jsx(Text, {
            marginBlockStart: "200",
            marginBlockEnd: "400",
            truncate: {
              lineClamp: 2
            },
            children: listing.description
          }), listing.highlights.length ? /*#__PURE__*/_jsx(Highlight, {
            listing: listing
          }) : /*#__PURE__*/_jsx(Badges, {
            listing: listing
          })]
        })
      })
    })
  }));
}
function ListingIcon({
  listing
}) {
  const lazyImage = useLazyImage();
  if (listing.products[0].productType === 'SOLUTIONS_PARTNER_PROFILE' && listing.iconUrl) {
    return /*#__PURE__*/_jsx(Image, {
      objectFit: "contain",
      src: listing.iconUrl,
      maxBlockSize: "100%",
      padding: "600"
    });
  }
  const icon = /*#__PURE__*/_jsx(AgentIconWithFallback, {
    iconUrl: listing.iconUrl,
    loading: lazyImage,
    marginBlockEnd: "0",
    name: listing.listingName
    // accomodate the width of the border (if it exists)
    ,
    size: listing.iconUrl ? 90 : 86
  });
  return listing.iconUrl ? icon :
  /*#__PURE__*/
  // add a border because otherwise it clashes with the gray background and looks ugly
  _jsx(View, {
    borderColor: "border-accent-magenta-default",
    borderWidth: "200",
    borderRadius: "400",
    borderStyle: "solid",
    overflow: "hidden",
    children: icon
  });
}
function Highlight({
  listing
}) {
  var _listing$highlights$;
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    backgroundColor: "fill-accent-purple-subtle-alt",
    borderRadius: "100",
    gap: "200",
    marginBlockStart: "auto",
    paddingBlock: "100",
    paddingInline: "200",
    children: [/*#__PURE__*/_jsx(IconHolder, {
      color: "fill-accent-purple-pressed",
      icon: BulbIcon,
      marginBlockEnd: "50",
      padding: "0"
    }), /*#__PURE__*/_jsx(Text, {
      color: "fill-accent-purple-pressed",
      children: (_listing$highlights$ = listing.highlights[0]) === null || _listing$highlights$ === void 0 ? void 0 : _listing$highlights$.highlightText
    })]
  });
}
const DISPLAYED_PRODUCT_FEATURES = ['MCP_SERVER'];
function Badges({
  listing
}) {
  var _listing$reviewSummar;
  const product = listing.products[0];
  const rating = (_listing$reviewSummar = listing.reviewSummary) === null || _listing$reviewSummar === void 0 ? void 0 : _listing$reviewSummar.overallAdjustedRating;
  const partnerTierContent = getPartnerTierContent(listing);
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    flexWrap: "wrap",
    gap: "200",
    marginBlockStart: "auto",
    children: [partnerTierContent !== null && partnerTierContent !== void 0 && partnerTierContent.icon ? /*#__PURE__*/_jsx(Badge, {
      use: partnerTierContent.badgeUse,
      children: /*#__PURE__*/_jsxs(Flex, {
        gap: "100",
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(IconHolder, {
          color: partnerTierContent.iconColor,
          icon: partnerTierContent.icon,
          padding: "0",
          size: "100"
        }), partnerTierContent.text]
      })
    }) : /*#__PURE__*/_jsx(Badge, {
      use: "koala",
      children: listing.displayTagLabel
    }), product.worksWithBreeze && /*#__PURE__*/_jsx(BreezeBadge, {}), product.productFeatures.filter(pf => pf.type && DISPLAYED_PRODUCT_FEATURES.includes(pf.type)).map(pf => /*#__PURE__*/_jsx(Badge, {
      use: "koala",
      children: pf.name
    }, `${String(pf.type)}-${pf.name}`)), product.productType === 'SOLUTIONS_PARTNER_PROFILE' && rating !== undefined && /*#__PURE__*/_jsx(Badge, {
      use: "koala",
      display: "flex",
      flexDirection: "row",
      gap: "400",
      children: /*#__PURE__*/_jsxs(Flex, {
        gap: "100",
        alignItems: "center",
        children: [rating.toFixed(1), /*#__PURE__*/_jsx(IconHolder, {
          color: "icon-core-subtle",
          icon: FavoriteIcon,
          marginBlockEnd: "25",
          padding: "0"
        })]
      })
    })]
  });
}